import React, {
  useContext, useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import format from 'string-template';

import DialogModal from '../../../../components/DialogModal';
import Button from '../../../../components/Button';
import User from '../../../../Model/User';
import InternalAssignment from '../../../../Model/InternalAssignment';
import LoadingOverlay from '../../../components/LoadingOverlay';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import useToast from '../../../hooks/useToast';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';
import ConfirmationModal from '../../../../components/v2/ConfirmationModal';
import { AssignmentRole } from '../../../../utils/internalAssignments';
import { PoolMembershipAction, capturePoolMembershipChange } from '../../../utils/poolTelemetry';
import {
  Title, Description, StyledDialogContent, ButtonContainer, GroupContainer, GroupHeader, GroupList, StyledTextField,
} from './styles';
import texts from './texts';

const AddAssignmentModal = ({
  role,
  coachName,
  coachId,
  isOpen,
  onClose,
  internalAssignments,
  isLiaison = false,
  description,
  onAssigned,
  skipPoolRemovalConfirmation,
}) => {
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [note, setNote] = useState('');
  const [isPoolRemovalConfirmationOpen, setIsPoolRemovalConfirmationOpen] = useState(false);
  const isComponentMountedRef = useComponentMounted();
  const { userId: loggedInUserId, userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchInternalUsers = async () => {
      setIsLoading(true);
      try {
        const users = await User.getInternalUsers();

        if (isComponentMountedRef.current) {
          const unAssignedUsers = users.filter(
            (user) => !internalAssignments.some((assignment) => assignment.userId === user.id),
          );
          setInternalUsers(unAssignedUsers);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          showToast(format(texts.errorLoadingUsers, { error: error.message }), { error: true });
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    fetchInternalUsers();
  }, [
    isComponentMountedRef,
    internalAssignments,
    showToast,
  ]);

  const addAssignment = async (isCoachPooled) => {
    setIsLoading(true);
    setIsPoolRemovalConfirmationOpen(false);
    const user = internalUsers.find((u) => u.id === selectedUserId);
    try {
      await InternalAssignment.addInternalAssignment(coachId, user.id, user.firstName, role, loggedInUserId, isLiaison);

      if (role === AssignmentRole.CAM) {
        await CoachHistory.addCoachHistoryItem({
          type: HistoryEventType.CAM_ASSIGNMENT_UPDATED,
          coachId,
          actionedBy: loggedInUserId,
          actionedByUserName: userDoc?.name,
          metadata: {
            previousValue: '',
            newValue: {
              userId: user.id,
              userName: user.firstName,
            },
            ...(note && { note }),
          },
        });
      }
    } catch (error) {
      showToast(format(texts.errorAddingAssignment, { error: error.message }), { error: true });
      return;
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        onClose();
      }
    }

    if (isCoachPooled) {
      capturePoolMembershipChange(loggedInUserId, coachId, PoolMembershipAction.REMOVED);
    }

    onAssigned();
  };

  /*
    Pool membership is read here rather than reused from a fetch made when the modal opened. The
    modal can sit open while another admin pools this coach, and addInternalAssignment re-queries at
    commit time — so a gate on the stale value would disable the POOL row with neither the
    confirmation nor the telemetry. Failing this read blocks the assignment: assigning without
    knowing whether it destroys a pool membership is the one outcome worth refusing.
  */
  const handleConfirm = async () => {
    setIsLoading(true);
    let isCoachPooled;
    try {
      const poolAssignments = await InternalAssignment.getConflictingAssignments(coachId, role);
      isCoachPooled = !!poolAssignments.length;
    } catch (error) {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        showToast(format(texts.errorCheckingPool, { error: error.message }), { error: true });
      }
      return;
    }

    if (!isComponentMountedRef.current) {
      return;
    }
    setIsLoading(false);

    if (isCoachPooled && !skipPoolRemovalConfirmation) {
      setIsPoolRemovalConfirmationOpen(true);
      return;
    }

    addAssignment(isCoachPooled);
  };

  const liaisonUsers = useMemo(() => {
    const usersCopy = [...internalUsers];
    const sortedUsers = usersCopy.sort((a, b) => Number(b.isLiaison) - Number(a.isLiaison));

    return sortedUsers.map((user) => ({
      label: `${user.name} (${user.email})`,
      value: user.id,
      isLiaison: user.isLiaison,
      name: user.name,
      email: user.email,
    }));
  }, [internalUsers]);

  const regularUsers = useMemo(() => {
    const usersCopy = [...internalUsers];
    return usersCopy.map((user) => ({
      label: `${user.name} (${user.email})`,
      value: user.id,
    }));
  }, [internalUsers]);

  return (
    <DialogModal
      open={isOpen}
      onClose={onClose}
    >
      <StyledDialogContent>
        {
          !isLiaison && (
            <Title>{`Assign ${role} user to ${coachName}`}</Title>
          )
        }
        {
          isLiaison && (
            <Title>{`Assign Liaison to ${coachName}`}</Title>
          )
        }
        {!!description && <Description>{description}</Description>}
        {
          !isLiaison && (
          <Autocomplete
            options={regularUsers}
            onChange={(_, option) => setSelectedUserId(option?.value ?? null)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select user to assign" />
            )}
          />
          )
        }
        {
          isLiaison && (
          <Autocomplete
            options={liaisonUsers}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_, option) => setSelectedUserId(option?.value ?? null)}
            groupBy={(option) => (option.isLiaison ? 'Leads' : 'ACs')}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select user to assign"
              />
            )}
            renderGroup={(params) => (
              <GroupContainer key={params.key}>
                <GroupHeader>
                  {params.group}
                </GroupHeader>
                <GroupList>{params.children}</GroupList>
              </GroupContainer>
            )}
          />
          )
        }
        {role === AssignmentRole.CAM && (
          <StyledTextField
            label={texts.notePlaceholder}
            variant="outlined"
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        <ButtonContainer>
          <Button
            secondary
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedUserId}>Confirm</Button>
        </ButtonContainer>
      </StyledDialogContent>
      <LoadingOverlay isLoading={isLoading} />
      {isPoolRemovalConfirmationOpen && (
        <ConfirmationModal
          open
          onClose={() => setIsPoolRemovalConfirmationOpen(false)}
          onConfirm={() => addAssignment(true)}
          title={format(texts.poolRemovalTitle, { coach: coachName })}
          description={format(texts.poolRemovalDescription, { coach: coachName })}
        />
      )}
    </DialogModal>
  );
};

AddAssignmentModal.propTypes = {
  role: PropTypes.string.isRequired,
  coachName: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  internalAssignments: PropTypes.array,
  isLiaison: PropTypes.bool.isRequired,
  description: PropTypes.string,
  onAssigned: PropTypes.func,
  skipPoolRemovalConfirmation: PropTypes.bool,
};

AddAssignmentModal.defaultProps = {
  internalAssignments: [],
  description: '',
  onAssigned: () => {},
  skipPoolRemovalConfirmation: false,
};

export default AddAssignmentModal;
